import type { PolicyDocument, PolicySection, PolicySubsection } from './types';

/** Stable URL slug from a section title (strips leading numbers). */
export function slugifyPolicyTitle(title: string): string {
  return title
    .replace(/^\d+(\.\d+)*\.?\s*/, '')
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export interface PolicyTocEntry {
  id: string;
  label: string;
  level: 2 | 3;
}

export function buildPolicyToc(sections: PolicySection[]): PolicyTocEntry[] {
  const entries: PolicyTocEntry[] = [];
  for (const section of sections) {
    const sectionId = section.id ?? slugifyPolicyTitle(section.title);
    entries.push({ id: sectionId, label: section.title, level: 2 });
    section.subsections?.forEach((sub) => {
      const subId = sub.id ?? `${sectionId}-${slugifyPolicyTitle(sub.title)}`;
      entries.push({ id: subId, label: sub.title, level: 3 });
    });
  }
  return entries;
}

export function resolveSectionId(section: PolicySection, index: number): string {
  return section.id ?? (slugifyPolicyTitle(section.title) || `section-${index + 1}`);
}

export function resolveSubsectionId(
  section: PolicySection,
  sectionIndex: number,
  sub: PolicySubsection,
  subIndex: number,
): string {
  const parentId = resolveSectionId(section, sectionIndex);
  return sub.id ?? (`${parentId}-${slugifyPolicyTitle(sub.title)}` || `section-${sectionIndex + 1}-${subIndex + 1}`);
}

export function enrichPolicyDocument(doc: PolicyDocument): PolicyDocument {
  return {
    ...doc,
    sections: doc.sections.map((section, si) => ({
      ...section,
      id: resolveSectionId(section, si),
      subsections: section.subsections?.map((sub, sj) => ({
        ...sub,
        id: resolveSubsectionId(section, si, sub, sj),
      })),
    })),
  };
}
